import { useLoaderData } from 'react-router';
import { ArrowDown } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { lookbookHref } from '~/modules/project/lookbook/lookbook-market';
import { useGatedDownloads } from '@modules/product/hook/use-gated-downloads';
import { DownloadSurface } from 'generated/graphql';
import { LookbookFrame } from '~/modules/project/lookbook/LookbookFrame';

/** The frame is half the row, so the image is asked for at half the anchor. */
const FRAME_SIZES = getImagesSizes({ desktop: 960, mobile: 414 });

/**
 * The pill that rides on the frame — the product page's white pill, lifted
 * off the image by a soft shadow.
 */
const FRAME_CONTROL = cn(
    'pointer-events-auto inline-flex items-center bg-white text-[#181818] no-underline',
    'font-sans font-medium transition-colors hover:bg-white/90',
    '[box-shadow:0_2px_8px_0_rgba(0,0,0,0.08),0_8px_24px_0_rgba(0,0,0,0.06)]',
);

/**
 * The project's own image, beside About the Project.
 *
 * Its thumbnail — the same picture the project carries on a browse grid —
 * desaturated, because this band is the page's editorial column rather than
 * its gallery: the colour photography belongs to the banner above, and a
 * second full-colour hero here would compete with it. The gallery and the
 * viewer both still show it in colour.
 *
 * The project's lookbook stands on it, its cover drawn at half the frame's
 * width, and the picture is the download: the reader sees the book and takes
 * it with one click, and the pill in the corner says so on hover. The gallery
 * further down is where the photographs are looked at.
 *
 * Behind the sign-in wall the product files stand behind, and through the same
 * gate: a guest's click opens it on the download intent and the book is handed
 * back on the far side. Signed in, the picture is a plain download link, so the
 * reader who has already paid the toll never meets it twice.
 */
export const AboutProjectImage = () => {
    const { project } = useLoaderData<typeof loader>();
    const src = project.Thumbnail?.desktop;
    const href = lookbookHref(project.URLSegment);
    const cover = {
        title: project.ProjectHeroTitle || project.Title,
        professional: project.Professional?.Title ?? '',
        imageSrc:
            project.Banner?.imageUrl ||
            project.FacebookPreviewImage ||
            undefined,
    };
    // The book costs what every other file costs: an account, and the lead
    // that taking it earns the practice.
    const { isGated, handleGatedDownload } = useGatedDownloads({
        surface: DownloadSurface.ProjectLookbook,
        libraryItemID: project.ID,
        professionalID: project.Professional?.ID ?? 0,
        itemImage: cover.imageSrc,
        // The gate hands the book back looking like the thing that was
        // clicked — the book on its picture — rather than a crop of the
        // banner the reader is already looking at.
        lookbookArtwork: { ...cover, frameSrc: src ?? undefined },
    });
    if (!src) return null;

    const frameClass =
        'group/frame relative block w-full cursor-pointer text-left no-underline hover:no-underline';
    const label = 'Download the Project Lookbook';
    const frame = (
        <LookbookFrame
            className="w-full rounded-[2rem]"
            picture={
                <Image
                    src={src}
                    alt={project.Title}
                    fill
                    fit="cover"
                    sizes={FRAME_SIZES}
                    fadeIn={false}
                    className="grayscale"
                />
            }
            cover={cover}
            // The book rises a touch under the pointer.
            coverClassName="transition-transform duration-300 ease-out group-hover/frame:-translate-y-[calc(50%+0.5rem)] motion-reduce:transition-none"
        >
            {/* Not a control of its own — the picture takes the click. */}
            <span className="pointer-events-none absolute bottom-0 left-0 max-md:p-4 md:p-[1.5rem]">
                <span
                    className={cn(
                        FRAME_CONTROL,
                        'gap-[0.5rem] rounded-[var(--radius-btn-l)] max-md:h-11 max-md:rounded-[var(--radius-btn-m)] max-md:px-4 max-md:text-sm md:h-[3.25rem] md:px-[1.25rem] md:text-[1rem]',
                        'opacity-0 transition-opacity duration-200 group-hover/frame:opacity-100 group-focus-visible/frame:opacity-100 motion-reduce:transition-none',
                    )}
                >
                    <ArrowDown
                        aria-hidden="true"
                        className="shrink-0 max-md:size-4 md:size-[1.125rem]"
                    />
                    Download Lookbook
                </span>
            </span>
        </LookbookFrame>
    );

    return (
        // The column it sits in sets the width; About the Project measures
        // that column to crop the copy beside it.
        <div className="w-full max-md:mb-8">
            {isGated ? (
                // A button, not an anchor the click cancels: an href a guest
                // can open in a new tab is a gate with a way around it.
                <button
                    type="button"
                    onClick={() => handleGatedDownload(href)}
                    aria-label={label}
                    className={frameClass}
                >
                    {frame}
                </button>
            ) : (
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    download=""
                    aria-label={label}
                    className={frameClass}
                >
                    {frame}
                </a>
            )}
        </div>
    );
};
